import { AppState } from '../types';
import { COLORS } from '../utils/constants';
import { formatGauge, formatLength } from '../utils/length';
import { buildHarnessSvg } from './harnessSvg';
import {
  Bom,
  ConnectorPinout,
  CutListRow,
  EndpointLabel,
  buildBom,
  buildCutList,
  buildPinouts
} from './reportData';

/** Options controlling the rendered report. */
export interface ReportOptions {
  /** Title shown in the report header and browser print dialog. */
  title?: string;
  /** Human-readable generation timestamp. Defaults to the current date and time. */
  generatedAt?: string;
}

const DEFAULT_TITLE = 'Cable Harness Report';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function endpointText(endpoint: EndpointLabel): string {
  const connector = escapeHtml(endpoint.connector);
  return endpoint.pin ? `${connector} &middot; ${escapeHtml(endpoint.pin)}` : connector;
}

/** A small colour chip, using a two-tone split when a stripe colour is set. */
function swatch(color?: string, stripeColor?: string): string {
  if (!color) {
    return '<span class="swatch swatch-none">&ndash;</span>';
  }
  const background = stripeColor
    ? `background:linear-gradient(90deg, ${escapeHtml(color)} 50%, ${escapeHtml(stripeColor)} 50%)`
    : `background:${escapeHtml(color)}`;
  return `<span class="swatch" style="${background}"></span>`;
}

function cutListSection(rows: CutListRow[]): string {
  if (rows.length === 0) {
    return '';
  }
  const body = rows
    .map(
      row => `
      <tr>
        <td class="num">${row.index}</td>
        <td>${escapeHtml(row.wireName)}</td>
        <td>${endpointText(row.from)}</td>
        <td>${endpointText(row.to)}</td>
        <td>${swatch(row.color, row.stripeColor)}</td>
        <td>${row.gauge !== undefined ? formatGauge(row.gauge) : '&ndash;'}</td>
        <td>${row.wireType ? escapeHtml(row.wireType) : '&ndash;'}</td>
        <td class="num">${row.length > 0 ? formatLength(row.length) : '<span class="unmeasured">? mm</span>'}</td>
        <td>${row.note ? escapeHtml(row.note) : ''}</td>
      </tr>`
    )
    .join('');
  return `
    <section class="page-break">
      <h2>Wire cut list</h2>
      <table>
        <thead>
          <tr>
            <th class="num">#</th><th>Wire</th><th>From</th><th>To</th>
            <th>Colour</th><th>Gauge</th><th>Type</th><th class="num">Length</th><th>Note</th>
          </tr>
        </thead>
        <tbody>${body}</tbody>
      </table>
    </section>`;
}

function bomSection(bom: Bom): string {
  const connectorRows = bom.connectors
    .map(
      row => `
      <tr>
        <td>${escapeHtml(row.name)}</td>
        <td>${row.manufacturer ? escapeHtml(row.manufacturer) : '&ndash;'}</td>
        <td>${row.partNumber ? escapeHtml(row.partNumber) : '&ndash;'}</td>
        <td class="num">${row.quantity}</td>
      </tr>`
    )
    .join('');

  const wireRows = bom.wires
    .map(
      row => `
      <tr>
        <td>${row.gauge !== undefined ? formatGauge(row.gauge) : '&ndash;'}</td>
        <td>${row.wireType ? escapeHtml(row.wireType) : '&ndash;'}</td>
        <td>${swatch(row.color)}</td>
        <td class="num">${row.count}</td>
        <td class="num">${formatLength(row.totalLength)}</td>
      </tr>`
    )
    .join('');

  return `
    <section class="page-break">
      <h2>Bill of materials</h2>
      <p class="summary">
        ${bom.connectorCount} connector${bom.connectorCount === 1 ? '' : 's'} &middot;
        ${bom.wireCount} wire${bom.wireCount === 1 ? '' : 's'} &middot;
        ${formatLength(bom.totalWireLength)} total wire
      </p>
      <h3>Connectors</h3>
      <table>
        <thead><tr><th>Connector / type</th><th>Manufacturer</th><th>Part no.</th><th class="num">Qty</th></tr></thead>
        <tbody>${connectorRows || '<tr><td colspan="4" class="empty">None</td></tr>'}</tbody>
      </table>
      <h3>Wire</h3>
      <table>
        <thead><tr><th>Gauge</th><th>Type</th><th>Colour</th><th class="num">Count</th><th class="num">Total length</th></tr></thead>
        <tbody>${wireRows || '<tr><td colspan="5" class="empty">None</td></tr>'}</tbody>
      </table>
    </section>`;
}

function pinoutSection(pinouts: ConnectorPinout[]): string {
  if (pinouts.length === 0) {
    return '';
  }
  const blocks = pinouts
    .map(pinout => {
      const rows = pinout.rows
        .map(
          row => `
          <tr>
            <td class="num">${escapeHtml(row.pin)}</td>
            <td>${row.name ? escapeHtml(row.name) : ''}</td>
            <td>${row.wire ? `${swatch(row.color)} ${escapeHtml(row.wire)}` : ''}</td>
            <td>${row.destination ? endpointText(row.destination) : ''}</td>
          </tr>`
        )
        .join('');
      const heading = pinout.type
        ? `${escapeHtml(pinout.connector)} <span class="type">(${escapeHtml(pinout.type)})</span>`
        : escapeHtml(pinout.connector);
      return `
        <div class="pinout">
          <h3>${heading}</h3>
          <table>
            <thead><tr><th>Pin</th><th>Name</th><th>Wire</th><th>Destination</th></tr></thead>
            <tbody>${rows || '<tr><td colspan="4" class="empty">No pins</td></tr>'}</tbody>
          </table>
        </div>`;
    })
    .join('');
  return `
    <section class="page-break">
      <h2>Connector pinouts</h2>
      ${blocks}
    </section>`;
}

const STYLES = `
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; color: #1c2b2f; margin: 0; padding: 24px; }
  header { display: flex; align-items: baseline; justify-content: space-between;
    border-bottom: 3px solid ${COLORS.PRIMARY}; padding-bottom: 10px; margin-bottom: 20px; }
  header .brand { font-size: 22px; font-weight: 700; color: ${COLORS.HEADER}; letter-spacing: 0.5px; }
  header .brand span { color: ${COLORS.PRIMARY}; }
  header .meta { font-size: 12px; color: ${COLORS.SECONDARY}; text-align: right; }
  h1 { font-size: 18px; margin: 0 0 16px; }
  h2 { font-size: 15px; color: ${COLORS.HEADER}; border-bottom: 1px solid #dfe4e2;
    padding-bottom: 4px; margin: 0 0 12px; }
  h3 { font-size: 13px; color: ${COLORS.SECONDARY}; margin: 14px 0 6px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px; }
  th, td { text-align: left; padding: 4px 8px; border-bottom: 1px solid #e6eae8; vertical-align: top; }
  th { background: #f2f5f4; font-weight: 600; color: ${COLORS.HEADER}; }
  td.num, th.num { text-align: right; white-space: nowrap; }
  .swatch { display: inline-block; width: 14px; height: 14px; border-radius: 3px;
    border: 1px solid #b9c2bf; vertical-align: middle; }
  .swatch-none { border: none; color: #aab; }
  .unmeasured { color: ${COLORS.DANGER}; }
  .summary { font-size: 12px; color: ${COLORS.SECONDARY}; margin: 0 0 12px; }
  .type { color: ${COLORS.SECONDARY}; font-weight: 400; }
  .empty { color: #99a; font-style: italic; }
  .diagram { text-align: center; }
  .diagram svg { max-width: 100%; height: auto; border: 1px solid #e6eae8; border-radius: 6px; }
  .pinout { break-inside: avoid; margin-bottom: 12px; }
  section { margin-bottom: 24px; }
  .page-break { break-before: page; }
  @page { size: A4 landscape; margin: 12mm; }
  @media print { body { padding: 0; } .page-break { break-before: page; } }
`;

/**
 * Assembles the complete printable harness report as a standalone HTML document:
 * a schematic diagram followed by the cut list, bill of materials and pinouts.
 * Intended to be opened in a window and printed to PDF.
 */
export function buildReportHtml(state: AppState, options: ReportOptions = {}): string {
  const title = options.title ?? DEFAULT_TITLE;
  const generatedAt = options.generatedAt ?? new Date().toLocaleString();

  const svg = buildHarnessSvg(state);
  const cutList = cutListSection(buildCutList(state));
  const bom = bomSection(buildBom(state));
  const pinouts = pinoutSection(buildPinouts(state));

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>${STYLES}</style>
</head>
<body>
  <header>
    <div class="brand">KIWI <span>Harness</span></div>
    <div class="meta">${escapeHtml(title)}<br />${escapeHtml(generatedAt)}</div>
  </header>
  <section class="diagram">
    <h1>${escapeHtml(title)}</h1>
    ${svg}
  </section>
  ${cutList}
  ${bom}
  ${pinouts}
</body>
</html>`;
}
